import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react'
import { ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { createAppTheme } from '@/theme/muiTheme'
import { lightThemeTokens } from '@/theme/themes/lightTheme'
import { darkThemeTokens } from '@/theme/themes/darkTheme'
import { injectSemanticCSSVariables } from '@/theme/cssVariables'
import { STORAGE_KEYS } from '@/utils/constants'

type ThemeMode = 'light' | 'dark' | 'auto'

interface ThemeContextType {
  themeMode: ThemeMode
  actualMode: 'light' | 'dark'
  setThemeMode: (mode: ThemeMode) => void
  theme: Theme
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export function useThemeContext() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeContextProvider')
  }
  return context
}

interface ThemeContextProviderProps {
  children: ReactNode
}

export function ThemeContextProvider({ children }: ThemeContextProviderProps) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.THEME) as ThemeMode
    return saved || 'auto'
  })

  const getActualMode = (mode: ThemeMode): 'light' | 'dark' => {
    if (mode === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return mode
  }

  const [actualMode, setActualMode] = useState<'light' | 'dark'>(() => getActualMode(themeMode))

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode)
    localStorage.setItem(STORAGE_KEYS.THEME, mode)
    const newActualMode = getActualMode(mode)
    setActualMode(newActualMode)

    // Применяем CSS классы и стили
    applyModeToDom(newActualMode)
  }

  // Применяем тему при загрузке
  useEffect(() => {
    const newActualMode = getActualMode(themeMode)
    setActualMode(newActualMode)

    applyModeToDom(newActualMode)
  }, [themeMode])

  // Слушаем изменения системной темы для режима 'auto'
  useEffect(() => {
    if (themeMode === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = () => {
        const newActualMode = getActualMode('auto')
        setActualMode(newActualMode)

        applyModeToDom(newActualMode)
      }
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [themeMode])

  const theme = useMemo(() => {
    const tokens = actualMode === 'dark' ? darkThemeTokens : lightThemeTokens
    injectSemanticCSSVariables(tokens.semantic, tokens.base.opacity)
    return createAppTheme(actualMode)
  }, [actualMode])

  function applyModeToDom(mode: 'light' | 'dark') {
    const root = document.documentElement
    const body = document.body

    if (mode === 'dark') {
      root.classList.add('dark-theme')
      root.classList.remove('light-theme')
      body.style.backgroundColor = darkThemeTokens.semantic.colors.background
      body.style.color = darkThemeTokens.semantic.colors.textPrimary
    } else {
      root.classList.add('light-theme')
      root.classList.remove('dark-theme')
      body.style.backgroundColor = lightThemeTokens.semantic.colors.background
      body.style.color = lightThemeTokens.semantic.colors.textPrimary
    }
  }

  return (
    <ThemeContext.Provider value={{ themeMode, actualMode, setThemeMode, theme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
